import { api } from "@/lib/api";

/** Spoken DJ links ("Ton DJ"): fetches a line written by the backend from
 *  the user's stats, plays it on its own <audio> element and ducks the music
 *  underneath while it talks — like the Spotify DJ over a track's intro. */

export type DjLine = { text: string; audio_url: string };

const PREF = "sl.djVoice";
const lines = new Map<string, Promise<DjLine | null>>();
let voice: HTMLAudioElement | null = null;
let silentUrl: string | null = null;
let restoreFn: (() => void) | null = null;

export function djVoiceEnabled(): boolean {
  try { return localStorage.getItem(PREF) !== "0"; } catch { return true; }
}

export function setDjVoiceEnabled(on: boolean) {
  try { localStorage.setItem(PREF, on ? "1" : "0"); } catch { /* private mode */ }
  if (!on) stopDjVoice();
}

function el(): HTMLAudioElement {
  if (!voice) {
    voice = new Audio();
    voice.preload = "auto";
    voice.setAttribute("playsinline", "true");
  }
  return voice;
}

/** A few ms of silence as a WAV blob — playing it inside the click that
 *  starts the DJ unlocks this element for later, gesture-less playback
 *  (iOS Safari only lets an element autoplay once it has played in a tap). */
function silence(): string {
  if (silentUrl) return silentUrl;
  const n = 400;
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const w = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, "RIFF"); v.setUint32(4, 36 + n * 2, true); w(8, "WAVE"); w(12, "fmt ");
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, 8000, true); v.setUint32(28, 16000, true); v.setUint16(32, 2, true);
  v.setUint16(34, 16, true); w(36, "data"); v.setUint32(40, n * 2, true);
  silentUrl = URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
  return silentUrl;
}

/** Call synchronously from the click handler that launches the DJ. */
export function primeDjVoice() {
  if (!djVoiceEnabled()) return;
  const a = el();
  a.src = silence();
  a.volume = 0;
  a.play().then(() => { a.pause(); a.volume = 1; }).catch(() => { a.volume = 1; });
}

export function getDjLine(trackId: string, first: boolean): Promise<DjLine | null> {
  const key = `${trackId}|${first ? 1 : 0}`;
  let p = lines.get(key);
  if (!p) {
    p = api<DjLine>("/api/dj/line", { method: "POST", body: JSON.stringify({ track_id: trackId, first }) })
      .catch(() => null);
    lines.set(key, p);
    if (lines.size > 60) lines.delete(lines.keys().next().value as string);
  }
  return p;
}

function ramp(target: HTMLAudioElement, to: number, ms: number): Promise<void> {
  const from = target.volume;
  const t0 = performance.now();
  return new Promise((resolve) => {
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / ms);
      try { target.volume = Math.max(0, Math.min(1, from + (to - from) * k)); } catch { /* ignore */ }
      if (k < 1) requestAnimationFrame(step); else resolve();
    };
    requestAnimationFrame(step);
  });
}

/** Resolves once the music element is actually producing sound. */
export function waitPlaying(music: HTMLAudioElement, timeoutMs = 9000): Promise<boolean> {
  return new Promise((resolve) => {
    const t0 = Date.now();
    const tick = () => {
      if (!music.paused && music.currentTime > 0.2) return resolve(true);
      if (Date.now() - t0 > timeoutMs) return resolve(false);
      window.setTimeout(tick, 200);
    };
    tick();
  });
}

/** Talk over the start of the current track, music ducked to ~25 %. */
export async function speakDjLine(music: HTMLAudioElement, getVolume: () => number, line: DjLine) {
  stopDjVoice();
  const a = el();
  a.src = line.audio_url;
  a.volume = Math.min(1, Math.max(0.45, getVolume() + 0.1));
  let restored = false;
  const restore = () => {
    if (restored) return;
    restored = true;
    restoreFn = null;
    void ramp(music, getVolume(), 900);
  };
  restoreFn = () => { a.pause(); restore(); };
  a.onended = restore;
  a.onerror = restore;
  window.dispatchEvent(new CustomEvent("dj-line", { detail: line.text }));
  await ramp(music, getVolume() * 0.25, 350);
  try { await a.play(); } catch { restore(); }
}

export function stopDjVoice() {
  restoreFn?.();
}
